import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, ArrowRight, Upload, Send, Users, Code, Shield, Cloud, AlertCircle, CheckCircle, Headphones, Briefcase, Loader, X, Calendar, Building2, ExternalLink, Award, Target, Zap } from 'lucide-react';
import Header from "./header";
import Footer from './footer';
const config = window.APP_CONFIG;
const apiUrl = config.API_URL; 

const CareersPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    cv: null,
  });
  // const apiUrl = process.env.REACT_APP_API_URL || 'http://13.202.42.97:81/bmrapi/api';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredJob, setHoveredJob] = useState(null);
  const [errors, setErrors] = useState({});
  const [formStatus, setFormStatus] = useState('');
  const [jobOpenings, setJobOpenings] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true);
  const [jobsError, setJobsError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Reference for the application form
  const applicationFormRef = useRef(null);

  const handleCardClick = (job) => {
    setSelectedJob(job);
    setShowPopup(true);
  };

  const handleApplyNowClick = (e, job) => {
    e.stopPropagation(); // Prevent card click from triggering
    setFormData(prev => ({
      ...prev,
      position: job.title
    }));
    
    // Smooth scroll to application form
    if (applicationFormRef.current) {
      applicationFormRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedJob(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getJobIcon = (title) => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('developer') || titleLower.includes('development')) {
      return <Code className="w-6 h-6" />;
    } else if (titleLower.includes('devops') || titleLower.includes('cloud')) {
      return <Cloud className="w-6 h-6" />;
    } else if (titleLower.includes('security') || titleLower.includes('cyber')) {
      return <Shield className="w-6 h-6" />;
    } else if (titleLower.includes('support') || titleLower.includes('help')) {
      return <Headphones className="w-6 h-6" />;
    } else if (titleLower.includes('designer') || titleLower.includes('ui') || titleLower.includes('ux')) {
      return <Users className="w-6 h-6" />;
    } else {
      return <Briefcase className="w-6 h-6" />;
    }
  };

  const getJobColor = (title, index) => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('developer') || titleLower.includes('development')) {
      return {
        gradient: "from-blue-500 via-blue-600 to-indigo-700",
        solid: "bg-blue-500",
        light: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700"
      };
    }
    if (titleLower.includes('devops') || titleLower.includes('cloud')) {
      return {
        gradient: "from-green-500 via-emerald-600 to-teal-700",
        solid: "bg-green-500",
        light: "bg-green-50",
        border: "border-green-200",
        text: "text-green-700"
      };
    }
    if (titleLower.includes('security') || titleLower.includes('cyber')) {
      return {
        gradient: "from-red-500 via-red-600 to-rose-700",
        solid: "bg-red-500",
        light: "bg-red-50",
        border: "border-red-200",
        text: "text-red-700"
      };
    }
    if (titleLower.includes('support') || titleLower.includes('help')) {
      return {
        gradient: "from-orange-500 via-orange-600 to-amber-700",
        solid: "bg-orange-500",
        light: "bg-orange-50",
        border: "border-orange-200",
        text: "text-orange-700"
      };
    }
    if (titleLower.includes('designer') || titleLower.includes('ui') || titleLower.includes('ux')) {
      return {
        gradient: "from-purple-500 via-purple-600 to-indigo-700",
        solid: "bg-purple-500",
        light: "bg-purple-50",
        border: "border-purple-200",
        text: "text-purple-700"
      };
    }
    
    const colorOptions = [
      {
        gradient: "from-pink-500 via-rose-600 to-red-700",
        solid: "bg-pink-500",
        light: "bg-pink-50",
        border: "border-pink-200",
        text: "text-pink-700"
      },
      {
        gradient: "from-cyan-500 via-blue-600 to-indigo-700",
        solid: "bg-cyan-500",
        light: "bg-cyan-50",
        border: "border-cyan-200",
        text: "text-cyan-700"
      },
      {
        gradient: "from-yellow-500 via-orange-600 to-red-700",
        solid: "bg-yellow-500",
        light: "bg-yellow-50",
        border: "border-yellow-200",
        text: "text-yellow-700"
      }
    ];
    
    return colorOptions[index % colorOptions.length];
  };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setIsLoadingJobs(true);
        const response = await fetch(`${apiUrl}/vacancy`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('API Response:', data);
        
        const jobsArray = data.vacancies || data || [];
        
        const transformedJobs = Array.isArray(jobsArray) ? jobsArray.map((job, index) => {
          const colors = getJobColor(job.JobProfile || job.Industry || '', index);
          return {
            id: job.Id || index + 1,
            title: job.JobProfile || 'Position not specified',
            location: job.Location || 'Location TBD',
            type: job.JobType || job.WorkMode || 'Full Time',
            JobDescription: job.JobDescription || 'Job description not available',
            JobProfile: job.JobProfile || 'Position not specified',
            Location: job.Location || 'Location TBD',
            JobType: job.JobType || job.WorkMode || 'Full Time',
            Industry: job.Industry || 'Not specified',
            WorkExperience: job.WorkExperience || 'Experience not specified',
            WorkMode: job.WorkMode || 'Not specified',
            WorkingDays: job.WorkingDays || 'Not specified',
            OpeningDate: job.OpeningDate || job.CreatedOn || new Date().toISOString(),
            AboutCompany: job.AboutCompany || 'Company information not available',
            CompanyRequirements: job.CompanyRequirenments || 'Requirements not specified',
            CompanyResponsibilities: job.CompanyResponsiblites || 'Responsibilities not specified',
            CreatedOn: job.CreatedOn || new Date().toISOString(),
            IsDeleted: job.IsDeleted || false,
            icon: getJobIcon(job.JobProfile || job.Industry || ''),
            colors: colors
          };
        }) : [];
        
        console.log('Transformed Jobs:', transformedJobs);
        setJobOpenings(transformedJobs);
        setJobsError(null);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setJobsError(err.message);
        setJobOpenings([]);
      } finally {
        setIsLoadingJobs(false);
      }
    };

    fetchJobs();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    return phoneRegex.test(cleanPhone);
  };

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = 'Name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = 'Name can only contain letters and spaces';
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = 'Email address is required';
        } else if (!validateEmail(value.trim())) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'phone':
        if (value.trim() && !validatePhone(value.trim())) {
          error = 'Please enter a valid phone number';
        }
        break;

      case 'position':
        if (!value.trim()) {
          error = 'Position is required';
        }
        break;

      case 'cv':
        if (!value) {
          error = 'Please upload your CV';
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (allowedTypes.includes(file.type)) {
        setFormData(prev => ({
          ...prev,
          cv: file
        }));
        setErrors(prev => ({
          ...prev,
          cv: ''
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          cv: 'Please upload a PDF, DOC, or DOCX file'
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    setFormStatus('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('Name', formData.name);
      formDataToSend.append('EmailId', formData.email);
      formDataToSend.append('PhoneNo', formData.phone);
      formDataToSend.append('ApplyingFor', formData.position);
      
      if (formData.cv) {
        formDataToSend.append('UploadCv', formData.cv);
      }

      console.log('Sending FormData with file:', formData.cv?.name);

      const response = await fetch(`${apiUrl}/joinus`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Success:', result);
        setFormStatus('success');
        
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          cv: null,
        });
        
        setErrors({});
        setTimeout(() => setFormStatus(''), 5000);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setFormStatus('error');
        setTimeout(() => setFormStatus(''), 5000);
      }
    } catch (error) {
      console.error('Network error:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderJobListings = () => {
    if (isLoadingJobs) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Loader className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Opportunities...</h2>
            <p className="text-gray-600">Please wait while we fetch the latest job openings</p>
          </div>
        </div>
      );
    }

    if (jobsError) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center bg-red-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-800 mb-2">Unable to Load Job Openings</h2>
            <p className="text-red-600 mb-4">Error: {jobsError}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    if (!jobOpenings || jobOpenings.length === 0) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Open Positions</h2>
            <p className="text-gray-600">We don't have any job openings at the moment. Please check back later!</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-6">
              We have {jobOpenings.length} open position{jobOpenings.length !== 1 ? 's' : ''}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">Find your perfect role and grow with us in an innovative environment</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {jobOpenings.map((job, index) => (
              <div
                key={job.id}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer transition-all duration-500 hover:shadow-2xl transform ${
                  hoveredJob === job.id ? 'scale-105' : ''
                } border ${job.colors.border}`}
                onMouseEnter={() => setHoveredJob(job.id)}
                onMouseLeave={() => setHoveredJob(null)}
                onClick={() => handleCardClick(job)}
              >
                {/* Gradient Header */}
                <div className={`h-2 bg-gradient-to-r ${job.colors.gradient}`}></div>
                
                <div className="p-6">
                  {/* Header with Icon */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center">
                      <div className={`p-3 rounded-xl ${job.colors.light} ${job.colors.text}`}>
                        {job.icon}
                      </div>
                      <div className="ml-4">
                        <h3 className="text-xl font-bold text-gray-800 line-clamp-2">{job.title}</h3>
                        <p className={`text-sm font-medium ${job.colors.text} mt-1`}>{job.Industry}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Job Details */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-3 text-gray-400" />
                      <span className="text-sm">{job.location}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Clock className="w-4 h-4 mr-3 text-gray-400" />
                      <span className="text-sm">{job.type}</span>
                    </div>
                    {job.WorkExperience && (
                      <div className="flex items-center text-gray-600">
                        <Award className="w-4 h-4 mr-3 text-gray-400" />
                        <span className="text-sm">{job.WorkExperience}</span>
                      </div>
                    )}
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-4 h-4 mr-3 text-gray-400" />
                      <span className="text-sm">Posted: {formatDate(job.CreatedOn)}</span>
                    </div>
                  </div>
                  
                  {/* Apply Button */}
                  <button 
                    onClick={(e) => handleApplyNowClick(e, job)}
                    className={`w-full bg-gradient-to-r ${job.colors.gradient} text-white py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 font-semibold flex items-center justify-center group`}
                  >
                    Apply Now
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Enhanced Application Form */}
       

          {/* Enhanced Popup Modal */}
          {showPopup && selectedJob && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
              <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-hidden shadow-2xl">
                {/* Enhanced Header */}
                <div className={`bg-gradient-to-r ${selectedJob.colors.gradient} px-8 py-8 text-white relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-10">
                    {selectedJob.icon}
                  </div>
                  <div className="relative z-10">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h2 className="text-3xl font-bold mb-2">{selectedJob.title}</h2>
                        <div className="flex items-center gap-6 text-white/80">
                          <div className="flex items-center">
                            <Building2 className="w-4 h-4 mr-2" />
                            <span>{selectedJob.Industry}</span>
                          </div>
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-2" />
                            <span>{selectedJob.Location}</span>
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-2" />
                            <span>{selectedJob.JobType}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={closePopup}
                        className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-all duration-200"
                      >
                        <X className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Enhanced Content */}
                <div className="overflow-y-auto max-h-[calc(95vh-200px)]">
                  <div className="p-8">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <Calendar className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                        <p className="text-xs text-gray-600 mb-1">Posted</p>
                        <p className="font-semibold text-sm">{formatDate(selectedJob.CreatedOn)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <Users className="w-6 h-6 mx-auto mb-2 text-green-600" />
                        <p className="text-xs text-gray-600 mb-1">Experience</p>
                        <p className="font-semibold text-sm">{selectedJob.WorkExperience}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <Clock className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                        <p className="text-xs text-gray-600 mb-1">Work Mode</p>
                        <p className="font-semibold text-sm">{selectedJob.WorkMode}</p>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 text-center">
                        <Calendar className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                        <p className="text-xs text-gray-600 mb-1">Working Days</p>
                        <p className="font-semibold text-sm">{selectedJob.WorkingDays}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column */}
                      <div className="space-y-8">
                        {/* About Company */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6">
                          <div className="flex items-center mb-4">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 ml-3">About Our Company</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed">{selectedJob.AboutCompany}</p>
                        </div>

                        {/* Job Description */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
                          <div className="flex items-center mb-4">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <Target className="w-5 h-5 text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 ml-3">Job Description</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed">{selectedJob.JobDescription}</p>
                        </div>
                      </div>

                      {/* Right Column */}
                      <div className="space-y-8">
                        {/* Requirements */}
                        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6">
                          <div className="flex items-center mb-4">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              <Shield className="w-5 h-5 text-orange-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 ml-3">Requirements</h3>
                          </div>
                          <div className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedJob.CompanyRequirements}</div>
                        </div>

                        {/* Responsibilities */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
                          <div className="flex items-center mb-4">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Zap className="w-5 h-5 text-purple-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 ml-3">Key Responsibilities</h3>
                          </div>
                          <div className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedJob.CompanyResponsibilities}</div>
                        </div>
                      </div>
                    </div>

                    {/* Job Details Table */}
                    <div className="mt-8 bg-gray-50 rounded-2xl p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                        <div className="p-2 bg-gray-200 rounded-lg mr-3">
                          <Briefcase className="w-5 h-5 text-gray-600" />
                        </div>
                        Additional Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <p className="text-sm font-medium text-gray-500 mb-1">Job Profile</p>
                          <p className="font-semibold text-gray-800">{selectedJob.JobProfile}</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <p className="text-sm font-medium text-gray-500 mb-1">Location</p>
                          <p className="font-semibold text-gray-800">{selectedJob.Location}</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <p className="text-sm font-medium text-gray-500 mb-1">Job Type</p>
                          <p className="font-semibold text-gray-800">{selectedJob.JobType}</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <p className="text-sm font-medium text-gray-500 mb-1">Work Mode</p>
                          <p className="font-semibold text-gray-800">{selectedJob.WorkMode}</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <p className="text-sm font-medium text-gray-500 mb-1">Opening Date</p>
                          <p className="font-semibold text-gray-800">{formatDate(selectedJob.OpeningDate)}</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <p className="text-sm font-medium text-gray-500 mb-1">Industry</p>
                          <p className="font-semibold text-gray-800">{selectedJob.Industry}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                      <button 
                        onClick={(e) => {
                          closePopup();
                          handleApplyNowClick(e, selectedJob);
                        }}
                        className={`flex-1 bg-gradient-to-r ${selectedJob.colors.gradient} text-white py-4 px-8 rounded-2xl hover:shadow-xl transition-all duration-300 font-semibold text-lg flex items-center justify-center group transform hover:-translate-y-1`}
                      >
                        Apply for this Position
                        <ExternalLink className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </button>
                      <button 
                        onClick={closePopup}
                        className="sm:w-auto px-8 py-4 border-2 border-gray-300 rounded-2xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-semibold text-gray-700"
                      >
                        Close Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header/>
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        
        {/* Animated Rocket */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="rocket-container">
            <img 
              src="/rocket-moving.svg" 
              alt="Rocket"
              className="rocket-svg"
              width="100" 
              height="100"
            />
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center animate-fade-in">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-slide-up">
              Join Our Innovation Journey
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 animate-slide-up animation-delay-200">
              Build the future of technology with Anvitech
            </p>
            <div className="flex justify-center">
              <div className="bg-white bg-opacity-20 rounded-full p-8 animate-pulse-slow">
                <div className="bg-white bg-opacity-30 rounded-full p-6">
                  <Code className="w-12 h-12 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-slate-50 to-transparent"></div>
      </div>
      {/* Job Openings Section */}
      {renderJobListings()}

      {/* Application Form Section */}
      <div ref={applicationFormRef}  className="bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 animate-fade-in">
              Ready to Join Our Team?
            </h2>
            <p className="text-xl text-blue-100 animate-fade-in animation-delay-200">
              Submit your application and let's build something amazing together
            </p>
          </div>

          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-3xl p-8 shadow-2xl animate-fade-in-up animation-delay-400">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-blue-100">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 bg-white bg-opacity-20 border rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300 ${
                      errors.name 
                        ? 'border-red-400 border-opacity-60' 
                        : 'border-white border-opacity-30'
                    }`}
                    placeholder="Enter your full name"
                    required
                  />
                  {errors.name && (
                    <p className="text-red-300 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.name}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-blue-100">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 bg-white bg-opacity-20 border rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300 ${
                      errors.email 
                        ? 'border-red-400 border-opacity-60' 
                        : 'border-white border-opacity-30'
                    }`}
                    placeholder="Enter your email"
                    required
                  />
                  {errors.email && (
                    <p className="text-red-300 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-blue-100">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 bg-white bg-opacity-20 border rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300 ${
                      errors.phone 
                        ? 'border-red-400 border-opacity-60' 
                        : 'border-white border-opacity-30'
                    }`}
                    placeholder="Enter your phone number"
                    required
                  />
                  {errors.phone && (
                    <p className="text-red-300 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.phone}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-blue-100">Position Applying For</label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 bg-white bg-opacity-20 border rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-300 ${
                      errors.position 
                        ? 'border-red-400 border-opacity-60' 
                        : 'border-white border-opacity-30'
                    }`}
                    required
                  >
                    <option value="" className="text-gray-900">Select a position</option>
                    {jobOpenings.map(job => (
                      <option key={job.id} value={job.title} className="text-gray-900">
                        {job.title}
                      </option>
                    ))}
                  </select>
                  {errors.position && (
                    <p className="text-red-300 text-sm mt-1 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.position}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-blue-100">Upload Resume/CV</label>
                <div className="relative">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    id="cv-upload"
                  />
                  <label
                    htmlFor="cv-upload"
                    className={`flex items-center justify-center w-full px-4 py-6 bg-white bg-opacity-20 border-2 border-dashed rounded-xl cursor-pointer hover:bg-opacity-30 transition-all duration-300 ${
                      errors.cv 
                        ? 'border-red-400 border-opacity-60' 
                        : 'border-white border-opacity-30'
                    }`}
                  >
                    <div className="text-center">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-blue-200" />
                      <p className="text-blue-100">
                        {formData.cv ? formData.cv.name : 'Click to upload your CV'}
                      </p>
                      <p className="text-sm text-blue-200 mt-1">PDF, DOC, or DOCX files only</p>
                    </div>
                  </label>
                </div>
                {errors.cv && (
                  <p className="text-red-300 text-sm mt-1 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.cv}
                  </p>
                )}
              </div>

              <div className="pt-4">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit Application
                    </>
                  )}
                </button>
                
                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2 mt-4">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-700">Message sent successfully! We'll get back to you soon.</span>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2 mt-4">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <span className="text-red-700">Failed to send message. Please try again or contact us directly.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        
        .animate-slide-up {
          animation: slide-up 0.8s ease-out;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }
        
        .animate-pulse-slow {
          animation: pulse 3s ease-in-out infinite;
        }
        
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        
        .animation-delay-400 {
          animation-delay: 400ms;
        }
        
        .rocket-container {
          position: absolute;
          top: 0;
          right: 0;
          width: 100%;
          height: 100%;
        }
        
        .rocket-svg {
          position: absolute;
          animation: rocket-flight 8s linear infinite;
          filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.5));
        }
        
        @keyframes rocket-flight {
          0% {
            transform: translate(calc(100vw + 60px), calc(100vh + 60px)) rotate(45deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translate(-120px, -120px) rotate(45deg);
            opacity: 0;
          }
        }
        
        @media (max-width: 768px) {
          .rocket-svg {
            width: 40px;
            height: 40px;
          }
        }
      `}</style>
      <Footer/>
    </div>
  );
};

export default CareersPage;